import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { shippingState } from '@theme/armony-shipping-core';

const cartRoot = window.Shopify?.routes?.root || '/';
const money = (cents) => '$' + Math.ceil(cents / 100).toLocaleString('es-CO');
let refreshVersion = 0;

function paintSummary(element, amount, currency, requiresShipping) {
  const state = shippingState(amount, currency, element.dataset.country, requiresShipping);
  element.hidden = !state.eligible;
  if (!state.eligible) return;
  const labels = JSON.parse(element.querySelector('[data-shipping-labels]').textContent);
  element.querySelector('[data-estimate-shipping]').textContent = state.unlocked ? labels.free : money(state.shipping);
  element.querySelector('[data-estimate-total]').textContent = money(state.total);
  element.querySelector('[data-estimate-threshold]').textContent = state.unlocked ? labels.unlocked : labels.threshold;
}
class ArmonyShippingSummary extends HTMLElement {
  connectedCallback() {
    if (this.dataset.context !== 'product') return;
    paintSummary(this, Number(this.dataset.amount), this.dataset.currency, this.dataset.requiresShipping === 'true');
    this.onvariant = (event) => paintSummary(this, event.detail.price, this.dataset.currency, event.detail.requiresShipping !== false);
    this.addEventListener('armony:variant-estimate', this.onvariant);
  }
  disconnectedCallback() { this.removeEventListener('armony:variant-estimate', this.onvariant); }
}
if (!customElements.get('armony-shipping-summary')) customElements.define('armony-shipping-summary', ArmonyShippingSummary);

function paint(cart) {
  document.querySelectorAll('armony-shipping-summary[data-context="cart"]').forEach(element => paintSummary(element, cart.total_price, cart.currency, cart.requires_shipping));
  document.querySelectorAll('armony-shipping-progress').forEach((element) => {
    const state = shippingState(cart.total_price, cart.currency, element.dataset.country, cart.requires_shipping);
    element.hidden = !state.eligible;
    const message = element.querySelector('[data-shipping-message]');
    if (message) message.innerHTML = state.unlocked
      ? 'Tu pedido tiene <strong>envío gratis.</strong>'
      : `Te faltan <strong>${money(state.remaining)}</strong> para el envío gratis.`;
    element.querySelector('[data-shipping-fill]')?.style.setProperty('width', `${state.progress}%`);
    element.querySelector('[data-shipping-track]')?.setAttribute('aria-valuenow', String(Math.round(state.progress)));
  });
}

async function refresh() {
  const version = ++refreshVersion;
  try {
    const response = await fetch(`${cartRoot}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    if (version === refreshVersion) paint(cart);
  } catch {
    // Server-rendered totals remain available when the connection is interrupted.
  }
}

for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
  document.addEventListener(name, (event) => {
    event.promise?.then(() => refresh()).catch(() => {});
  });
}
window.addEventListener('pageshow', () => refresh());

// The custom ARMONY form uses the same cart events and drawer as the native theme.
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('.adp2-form')) return;
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return; // Keep normal Shopify form submission available.
  event.preventDefault();
  if (form.dataset.adding === 'true') return;
  const root = form.closest('[data-adp2]');
  const controls = [...root.querySelectorAll('[data-adp2-add],[data-adp2-sticky-button]')];
  const error = form.querySelector('[data-armony-cart-error]');
  const variant = form.querySelector('input[name="id"]')?.value;
  if (!variant || controls.some((button) => button.disabled)) return;
  const payload = new FormData(form);
  const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId).filter(Boolean))];
  payload.set('sections', sectionIds.join(','));
  payload.set('sections_url', window.location.pathname);
  const deferred = CartLinesUpdateEvent.createPromise();
  const options = [...root.querySelectorAll('[data-adp2-option-index]')];
  const optionStates = options.map((button) => button.disabled);
  form.dataset.adding = 'true';
  options.forEach((button) => { button.disabled = true; });
  controls.forEach((button) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
  if (error) error.hidden = true;
  form.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add', context: 'product',
    lines: [{ merchandiseId: variant, quantity: Number(payload.get('quantity')) || 1 }],
    promise: deferred.promise,
  }));
  let added = false;
  try {
    const response = await fetch(`${cartRoot}cart/add.js`, {
      method: 'POST', headers: { Accept: 'application/json' }, body: payload,
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'No pudimos agregar este producto. Intenta de nuevo.');
    added = true;
    const cartResponse = await fetch(`${cartRoot}cart.js`, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error('No pudimos actualizar el carrito.');
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: result.sections, items: cart.items, itemCount: cart.item_count, source: 'armony-product', didError: false },
    });
    paint(cart);
  } catch (failure) {
    deferred.reject(failure);
    if (added) {
      // Avoid suggesting a second add when the mutation succeeded but the refresh failed.
      window.location.assign(`${cartRoot}cart`);
      return;
    }
    if (error) { error.textContent = failure.message; error.hidden = false; }
  } finally {
    delete form.dataset.adding;
    options.forEach((button, index) => { button.disabled = optionStates[index]; });
    controls.forEach((button) => { button.disabled = false; button.removeAttribute('aria-busy'); });
  }
});

